import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, DollarSign, AlertCircle, Calendar, Home, Camera, Scan, FileText, ArrowRight } from 'lucide-react';
import { Card } from '../../components/v2/V2Components';
import InventoryService from '../../services/InventoryService';
import OnboardingService from '../../services/OnboardingService';
import HouseService from '../../services/HouseService';
import TaskService from '../../services/TaskService';
import { FEATURES } from '../../featureFlags';
import usePageView from '../../hooks/usePageView';
import { ANALYTICS_EVENTS } from '../../constants/analyticsEvents';

// Onboarding card configurations
const ONBOARDING_CARDS = [
    {
        key: 'first_house',
        icon: Home,
        title: 'Add Your First Property',
        description: 'Track inventory and maintenance for your home.',
        action: '/houses/add',
        actionLabel: 'Add Property',
        bgClass: 'bg-green-50 dark:bg-green-900/20',
        colorClass: 'text-green-600 dark:text-green-400'
    },
    {
        key: 'first_item',
        icon: Package,
        title: 'Add Your First Item',
        description: 'Start building your home inventory.',
        action: '/inventory',
        actionLabel: 'Add Item',
        bgClass: 'bg-blue-50 dark:bg-blue-900/20',
        colorClass: 'text-blue-600 dark:text-blue-400'
    },
    {
        key: 'first_item_scan',
        icon: Camera,
        title: 'Try Smart Scan',
        description: 'Quickly add items by scanning receipts or products.',
        action: '/inventory',
        actionLabel: 'Start Scan',
        bgClass: 'bg-purple-50 dark:bg-purple-900/20',
        colorClass: 'text-purple-600 dark:text-purple-400'
    },
    {
        key: 'first_room_scan',
        icon: Scan,
        title: 'Scan a Room',
        description: 'Map your space with 3D room scanning.',
        action: '/rooms',
        actionLabel: 'Scan Room',
        bgClass: 'bg-amber-50 dark:bg-amber-900/20',
        colorClass: 'text-amber-600 dark:text-amber-400'
    },
    {
        key: 'first_document',
        icon: FileText,
        title: 'Upload a Document',
        description: 'Store warranties, manuals, and important documents.',
        action: '/documents',
        actionLabel: 'Upload',
        bgClass: 'bg-teal-50 dark:bg-teal-900/20',
        colorClass: 'text-teal-600 dark:text-teal-400'
    }
];

/**
 * Nearest upcoming (today-or-later) due date among incomplete tasks, as a
 * short label — or an em dash when nothing qualifies (an overdue-only list
 * is NOT "next service"). Exported for unit tests.
 *
 * Dates are compared as LOCAL calendar days: the date part of the ISO
 * string is reparsed as local midnight, so a task due "today" stays due
 * today in negative-UTC-offset timezones (naive `new Date('YYYY-MM-DD')`
 * parses as UTC midnight and lands on yesterday west of Greenwich).
 */
export const computeNextServiceLabel = (tasks, now = new Date()) => {
    const today = new Date(now);
    today.setHours(0, 0, 0, 0);
    const upcoming = (Array.isArray(tasks) ? tasks : [])
        .filter(t => t.status !== 'Completed' && t.due_date)
        .map(t => {
            const [y, m, d] = String(t.due_date).slice(0, 10).split('-').map(Number);
            return new Date(y, (m || 1) - 1, d || 1);
        })
        .filter(d => !Number.isNaN(d.getTime()) && d >= today)
        .sort((a, b) => a - b);
    return upcoming.length
        ? upcoming[0].toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
        : '—';
};

const Dashboard = ({ user }) => {
    usePageView(ANALYTICS_EVENTS.DASHBOARD_VIEWED);
    const navigate = useNavigate();
    const [stats, setStats] = useState({
        itemCount: 0,
        totalValue: 0,
        alerts: 0,
        // Nearest upcoming maintenance due date; em dash until real data
        // loads (was a hardcoded 'Nov' placeholder pre-launch).
        nextService: '—'
    });
    const [loading, setLoading] = useState(true);
    // Initialize with default status so cards appear during loading
    const [onboardingStatus, setOnboardingStatus] = useState(
        OnboardingService.getDefaultStatus().milestones
    );
    const [primaryHouse, setPrimaryHouse] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [items, onboarding, house, tasks] = await Promise.all([
                    InventoryService.getAllItems(),
                    OnboardingService.getStatus(),
                    HouseService.getPrimaryHouse(),
                    // Maintenance may be gated off; a failed/absent fetch just
                    // leaves the em-dash placeholder.
                    FEATURES.maintenance ? TaskService.getTasks({ all: true }).catch(() => []) : Promise.resolve([])
                ]);

                const count = items.length;
                const value = items.reduce((acc, item) => {
                    const price = parseFloat(item.estimated_value || item.purchase_price);
                    return acc + (price || 0);
                }, 0);

                setStats(prev => ({
                    ...prev,
                    itemCount: count,
                    totalValue: value,
                    nextService: computeNextServiceLabel(tasks)
                }));
                // OnboardingService.getStatus() now returns defaults on error
                setOnboardingStatus(onboarding.milestones);
                setPrimaryHouse(house);
            } catch (error) {
                console.error("Failed to fetch dashboard data", error);
                // Keep default onboarding status on error so cards still appear
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    // True when the user's primary house is the auto-seeded placeholder.
    // The "Complete your property" CTA covers the same intent as the
    // existing first_house onboarding card, so we suppress the latter
    // to avoid showing two CTAs that mean the same thing. The backend
    // sets is_placeholder=true at seed time and flips it on the
    // placeholder→real transition in update_house.
    const isPlaceholderHouse = primaryHouse?.is_placeholder ?? false;

    // Filter to incomplete milestones - always have a valid status due to defaults
    const incompleteCards = ONBOARDING_CARDS.filter(card => {
        // Hide first_house when the placeholder CTA is showing — they mean the
        // same thing and stacking both is confusing.
        if (isPlaceholderHouse && card.key === 'first_house') return false;
        return !onboardingStatus[card.key]?.completed;
    });

    const QuickStat = ({ icon: Icon, value, label, colorClass, bgClass }) => (
        <Card className="p-4 flex flex-col items-center justify-center text-center space-y-2">
            <div className={`p-2 rounded-full ${bgClass} ${colorClass}`}>
                <Icon size={20} />
            </div>
            <span className="text-2xl font-bold text-[var(--text-main)] theme-transition">
                {loading ? '-' : value}
            </span>
            <span className="text-xs text-[var(--text-secondary)] uppercase tracking-wide theme-transition">
                {label}
            </span>
        </Card>
    );

    return (
        <div className="space-y-6 animate-fade-in">
            <header className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-[var(--text-main)] theme-transition">Welcome Home</h1>
                    <p className="text-[var(--text-secondary)] theme-transition">
                        {user ? `${user.username}'s Residence` : 'My House'}
                    </p>
                </div>
            </header>

            {/* Placeholder house CTA — surfaces only when the auto-seeded
                placeholder primary house has not yet been completed. */}
            {isPlaceholderHouse && (
                <Card
                    onClick={() => navigate('/houses/add')}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            // preventDefault on Space stops the page from
                            // scrolling when the user activates the CTA.
                            e.preventDefault();
                            navigate('/houses/add');
                        }
                    }}
                    className="p-5 cursor-pointer flex items-center justify-between bg-[var(--bg-card)] border border-[var(--border)] hover:opacity-90 transition theme-transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)]"
                    data-testid="complete-property-cta"
                >
                    <div className="flex items-center space-x-4">
                        <div className="p-3 rounded-lg bg-[var(--bg-card)] text-[var(--primary)] border border-[var(--border)]">
                            <Home size={22} />
                        </div>
                        <div>
                            <h3 className="font-semibold text-[var(--text-main)] theme-transition">
                                Finish setting up {primaryHouse?.name || 'your property'}
                            </h3>
                            <p className="text-sm text-[var(--text-secondary)] mt-1 theme-transition">
                                Add your address and details so items, rooms, and tasks have a home.
                            </p>
                        </div>
                    </div>
                    <ArrowRight size={20} className="text-[var(--primary)] flex-shrink-0" />
                </Card>
            )}

            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <QuickStat
                    icon={Package}
                    value={stats.itemCount}
                    label="Items"
                    bgClass="bg-blue-50 dark:bg-blue-900/20"
                    colorClass="text-blue-600 dark:text-blue-400"
                />
                <QuickStat
                    icon={DollarSign}
                    value={`$${stats.totalValue.toLocaleString()}`}
                    label="Total Value"
                    bgClass="bg-purple-50 dark:bg-purple-900/20"
                    colorClass="text-purple-600 dark:text-purple-400"
                />
                <QuickStat
                    icon={AlertCircle}
                    value={stats.alerts}
                    label="Alerts"
                    bgClass="bg-amber-50 dark:bg-amber-900/20"
                    colorClass="text-amber-600 dark:text-amber-400"
                />
                <QuickStat
                    icon={Calendar}
                    value={stats.nextService}
                    label="Next Service"
                    bgClass="bg-green-50 dark:bg-green-900/20"
                    colorClass="text-green-600 dark:text-green-400"
                />
            </div>

            {/* Getting Started / Onboarding */}
            {incompleteCards.length > 0 && (
                <section>
                    <h2 className="text-lg font-semibold text-[var(--text-main)] mb-4 theme-transition">Getting Started</h2>
                    <Card className="divide-y divide-[var(--border)]">
                        {incompleteCards.map((card) => {
                            const Icon = card.icon;
                            return (
                                <div key={card.key} className="p-4 flex items-start space-x-4">
                                    <div className={`p-2 ${card.bgClass} ${card.colorClass} rounded-lg`}>
                                        <Icon size={20} />
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="font-medium text-[var(--text-main)] theme-transition">{card.title}</h3>
                                        <p className="text-sm text-[var(--text-secondary)] mt-1 theme-transition">
                                            {card.description}
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => navigate(card.action)}
                                        className="text-sm text-[var(--primary)] font-medium hover:underline theme-transition"
                                    >
                                        {card.actionLabel}
                                    </button>
                                </div>
                            );
                        })}
                    </Card>
                </section>
            )}
        </div>
    );
};

export default Dashboard;
